<template>
  <InputGroup icon-position="left">
    <InputGroupAddon>
      <i :class="['pi', { 'pi-apple': platform === 'IOS' }, { 'pi-android': platform === 'ANDROID' }]" />
    </InputGroupAddon>
    <Skeleton v-if="loading" height="3rem" class="mb-2"></Skeleton>
    <MultiSelect
      v-if="multiple && canUserEdit && !loading"
      v-model="selected"
      :placeholder="placeholder"
      :options="options"
      option-label="label"
      option-value="value"
      :disabled="disabled"
      :max-selected-labels="1"
      @update:model-value="handleMultipleUpdate"
    >
      <template #option="slotProps">
        <div :class="$style.optionWrapper">
          <div>
            <div :class="$style.optionProductId">{{ slotProps.option.iap.productId }}</div>
            <div>{{ slotProps.option.iap.name }}</div>
          </div>
          <div>
            {{ slotProps.option.iap.pricing[0].value }}
            <span>
              {{ slotProps.option.iap.pricing[0].currency }}
            </span>
          </div>
        </div>
      </template>
    </MultiSelect>
    <Select
      v-else-if="!multiple && canUserEdit && !loading"
      v-model="selected"
      :options="options"
      filter
      :disabled="disabled"
      :filter-fields="['pricing', 'label']"
      option-label="label"
      option-value="value"
      :show-clear="canUserClear"
      :placeholder="placeholder"
      @update:model-value="handleUpdate"
    >
      <template #option="slotProps">
        <div :class="$style.optionWrapper">
          <div>
            <div :class="$style.optionProductId">{{ slotProps.option.iap.productId }}</div>
            <div>{{ slotProps.option.iap.name }}</div>
          </div>
          <div>
            {{ slotProps.option.iap.pricing[0].value }}
            <span>
              {{ slotProps.option.iap.pricing[0].currency }}
            </span>
          </div>
        </div>
      </template>
    </Select>
    <InputText
      v-if="!canUserEdit && !loading"
      :value="selectedIAPlabel"
      :disabled="true"
      :class="{ [$style.disabled]: true }"
    />
  </InputGroup>
</template>

<script setup lang="ts">
import { IapType, Platform, useGetInAppPurchasesQuery } from "@/graphql";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import Select from "primevue/select";
import Skeleton from "primevue/skeleton";
import type { PropType } from "vue";
import { computed, ref } from "vue";

const emits = defineEmits(["update", "update-multiple"]);

const props = defineProps({
  modelValue: {
    type: String as PropType<string>,
    required: false,
    default: null,
  },
  modelValues: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  platform: {
    type: String as PropType<Platform>,
    required: true,
  },
  gameId: {
    type: String,
    required: true,
  },
  canUserEdit: {
    type: Boolean,
    required: false,
    default: true,
  },
  canUserClear: {
    type: Boolean,
    required: false,
    default: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  nonConsumableIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const selected = ref(props.multiple ? props.modelValues : props.modelValue);

const { result, loading } = useGetInAppPurchasesQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    countryIso: "USA",
    platform: props.platform,
    type: [IapType.NonConsumable, IapType.Consumable],
    onlyOriginalIaPs: false,
  },
  {
    enabled: !!props.gameId,
    fetchPolicy: "network-only",
  }
);

const availableIAPs = computed(() => {
  if (props.nonConsumableIds.length === 0) {
    return result.value?.getInAppPurchases?.filter((iap) => iap?.storeItems?.length === 0);
  }

  return result.value?.getInAppPurchases?.filter((iap) => {
    const storeItemIds = iap?.storeItems?.map((storeItem) => storeItem?.storeItemId) || [];
    return JSON.stringify(storeItemIds?.sort()) === JSON.stringify([...props.nonConsumableIds].sort());
  });
});

type UndefinedOrNull<T> = T | undefined | null;
type IAPOption = {
  productId: UndefinedOrNull<string>;
  pricing: UndefinedOrNull<number>;
  value: UndefinedOrNull<string>;
  label: UndefinedOrNull<string>;
};

const sortIAP = (firstIAP: IAPOption, secondIAP: IAPOption) => {
  if (!!firstIAP.pricing && !!secondIAP.pricing) {
    if (firstIAP.pricing < secondIAP.pricing) {
      return -1;
    }
    if (firstIAP.pricing > secondIAP.pricing) {
      return 1;
    }
  }
  if (!firstIAP.productId || !secondIAP.productId) {
    return 0;
  }
  if (firstIAP.productId < secondIAP.productId) {
    return -1;
  }
  if (firstIAP.productId > secondIAP.productId) {
    return 1;
  }
  return 0;
};

const options = computed(() =>
  availableIAPs.value
    ?.map((iap) => {
      const usaPricing = iap?.pricing?.find((price) => price?.countryIso === "USA");
      return {
        label: `${iap?.productId} - ${usaPricing?.value} ${usaPricing?.currency}`,
        value: iap?.id,
        iap,
        pricing: usaPricing?.value,
        productId: iap?.productId,
      };
    })
    .sort(sortIAP)
);

const placeholder = computed(() =>
  props.platform === Platform.Ios ? "Select an App Store IAP ID" : "Select a Play Store IAP ID"
);

const selectedIAPlabel = computed(() => {
  const selectedIAp = availableIAPs.value?.find((iap) => iap?.id === props.modelValue);

  if (selectedIAp) {
    const usaPricing = selectedIAp?.pricing?.find((price) => price?.countryIso === "USA");
    return `${selectedIAp.productId} - ${usaPricing?.value} ${usaPricing?.currency}`;
  }

  return ``;
});

const handleUpdate = (selectedIAPId: string) => {
  const selectedIAP = availableIAPs.value?.find((iap) => iap?.id === selectedIAPId);
  const usaPricing = selectedIAP?.pricing?.find((price) => price?.countryIso === "USA");

  emits("update", {
    id: selectedIAPId,
    platform: props.platform,
    price: usaPricing?.value ? `${usaPricing.value} ${usaPricing.currency}` : "",
  });
};

const handleMultipleUpdate = (selectedIAPIds: string[]) => {
  const selectedIAPs = availableIAPs.value?.filter((iap) => selectedIAPIds.includes(iap?.id || ""));
  const selectedIAPPrices = selectedIAPs?.map((iap) => {
    const usaPricing = iap?.pricing?.find((price) => price?.countryIso === "USA");
    return usaPricing?.value ? `${usaPricing.value} ${usaPricing.currency}` : "";
  });

  emits("update-multiple", {
    ids: selectedIAPIds,
    platform: props.platform,
    prices: selectedIAPPrices,
  });
};
</script>

<style module>
.optionWrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.optionProductId {
  color: #909298;
  max-width: 400px;
  min-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.optionCurrency {
  color: #909298;
  size: 12px;
}

.disabled {
  opacity: 1;
  pointer-events: none;
}
</style>
